import React from 'react';
import { Link } from 'react-router-dom';
import { ComponentSkinProps } from '../../../../types/component-skin';
import { PriceDisplay } from '../../../common/PriceDisplay';
import SafeImage from '../../../common/SafeImage';

/**
 * 장바구니 기본 스킨
 */
const BasicCartSkin: React.FC<ComponentSkinProps> = ({ 
  data, 
  actions, 
  options, 
  mode, 
  utils,
  app,
  editor
}) => {
  const { t } = utils;
  
  // 데이터 추출
  const { 
    cartItems,
    loading,
    error,
    localQuantities,
    calculations,
    isUserLoggedIn,
    isAdminLoggedIn,
    withcookieData,
    userInfo,
    freeShippingLimit,
    title,
    emptyText,
    shippingFee,
    showStock
  } = data || {};
  
  // 액션 추출
  const {
    handleIncreaseQuantity,
    handleDecreaseQuantity,
    handleRemoveFromCart,
    handleCheckout,
    handleClearCart,
    calculateLevelPrice
  } = actions || {};
  
  // 옵션 추출 (사용자 설정 가능한 항목)
  const {
    primaryButtonColor = '#007bff',
    secondaryButtonColor = '#6c757d',
    dangerButtonColor = '#dc3545',
    priceColor = '#ff6b6b',
    summaryBackgroundColor = '#f8f9fa'
  } = options || {};
  
  // 로딩 상태 표시
  if (loading) {
    return (
      <div className="cart-component-wrapper">
        <div className="container">
          <div className="row cart-row">
            <div className="col-12">
              <div className="cart-loading">
                <p>{t('장바구니 정보를 불러오는 중입니다...')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <div className="cart-component-wrapper">
        <div className="container">
          <div className="row cart-row">
            <div className="col-12">
              <div className="cart-error">
                <p>{t('장바구니 정보를 불러오는데 실패했습니다')}: {typeof error === 'object' ? (error as any).message || JSON.stringify(error) : error}</p>
                <button 
                  className="retry-button"
                  onClick={() => {
                    // 장바구니 데이터 다시 불러오기
                    window.location.reload();
                  }}
                >
                  {t('다시 시도')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 필요
  if (!isUserLoggedIn && !isAdminLoggedIn) {
    return (
      <div className="cart-component-wrapper">
        <div className="container">
          <div className="row cart-row">
            <div className="col-12">
              <div className="cart-login-required">
                <p>{t('장바구니를 확인하려면 로그인이 필요합니다.')}</p>
                <Link to="/login" className="login-button">{t('로그인')}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 장바구니가 비어있는 경우
  if (!cartItems || (Array.isArray(cartItems) && cartItems.length === 0)) {
    return (
      <div className="cart-component-wrapper">
        <div className="container">
          <div className="row cart-row">
            <div className="col-12">
              <div className="cart-empty">
                <h2>{title}</h2>
                <p>{emptyText}</p>
                <Link to="/shopping" className="continue-shopping-button">
                  {t('쇼핑 계속하기')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 장바구니에 상품이 있는 경우
  return (
    <div className="cart-component-wrapper">
      <div className="container">
        <div className="row cart-row">
          <div className="col-12">
            <h2 className="cart-page-title">{title}</h2>
          </div>
        </div>
        <div className="row cart-row">
          {/* 장바구니 상품 목록 */}
          <div className="col-lg-8 col-md-12">
            <div className="cart-items-list">
              {cartItems.map((item: any) => {
                // 상품 정보와 가격 가져오기
                const product = item.product || {};
                const config = product.config || {};
                const discountedPrice = config.discounted_price ? Number(config.discounted_price) : 0;
                const defaultPrice = config.default_price ? Number(config.default_price) : 0;
                const basePrice = discountedPrice || defaultPrice;
                
                // 직급별 가격 계산
                const priceInfo = calculateLevelPrice(basePrice, product);
                const finalBasePrice = priceInfo.levelPrice;
                
                // Variant 추가 가격
                const additionalPrice = item.variant?.additionalPrice 
                  ? Number(item.variant.additionalPrice) 
                  : 0;
                
                // 최종 가격 (직급별 가격 + 추가 가격)
                const price = finalBasePrice + additionalPrice;
                
                // 수량은 로컬 상태에서 가져옴
                const count = localQuantities[item.id] || item.count || 1;
                
                return (
                  <div className="cart-item" key={item.id}>
                    <div className="cart-item-inner">
                      <div className="cart-item-top">
                        <div className="cart-item-image">
                          <Link to={`/products/${product.id}`}>
                            {/* @ts-ignore - SafeImage 컴포넌트 타입 오류 무시 */}
                            <SafeImage
                              src={config.img_url}
                              alt={product.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                              placeholderStyle={{ width: '100%', height: '100%', background: '#cccccc', borderRadius: '4px' }}
                            />
                          </Link>
                        </div>
                        <div className="cart-item-details">
                          <div className="cart-item-title">
                            <Link to={`/products/${product.id}`}>
                              {t(product.title) || t('상품명 없음')}
                            </Link>
                          </div>
                          
                          {/* 옵션 정보 표시 */}
                          {item.variant && item.variant.options && item.variant.options.length > 0 && (
                            <div className="cart-item-options">
                              {item.variant.options.map((opt: any, index: number) => (
                                <span key={index} className="option-item">
                                  {opt.optionValue.optionGroup.name}: {opt.optionValue.value}
                                  {index < item.variant.options.length - 1 && ', '}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* 레거시 옵션 정보 표시 (options 객체가 있는 경우) */}
                          {!item.variant && item.options && Object.keys(item.options).length > 0 && (
                            <div className="cart-item-options">
                              {(() => {
                                // sku, price, pv 등을 제외한 실제 옵션만 표시
                                const optionEntries = Object.entries(item.options)
                                  .filter(([key]) => !['sku', 'price', 'pv'].includes(key));
                                
                                if (optionEntries.length === 0) return null;
                                
                                return optionEntries.map(([key, value], index) => (
                                  <span key={key} className="option-item">
                                    {`${key}: ${value}${index < optionEntries.length - 1 ? ', ' : ''}`}
                                  </span>
                                ));
                              })()}
                            </div>
                          )}
                          
                          <div className="cart-item-price">
                            {/* 직급별 가격이 있는 경우 */}
                            {priceInfo.discount > 0 && priceInfo.levelName ? (
                              <>
                                <div className="level-price-info">
                                  <span className="level-badge-small">{priceInfo.levelName}</span>
                                  <span className="original-price-small">
                                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                                    <PriceDisplay price={basePrice} />
                                  </span>
                                  <span className="arrow">→</span>
                                  <span className="level-price-small">
                                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                                    <PriceDisplay price={finalBasePrice} />
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                                <PriceDisplay price={finalBasePrice} />
                              </>
                            )}
                            
                            {/* Variant 추가 가격 표시 */}
                            {item.variant && Number(item.variant.additionalPrice) > 0 && (
                              <span className="variant-additional-price">
                                (+₩{Number(item.variant.additionalPrice).toLocaleString()})
                              </span>
                            )}
                          </div>
                          {showStock && config.stock_count !== undefined && (
                            <div className="cart-item-stock">
                              {t('재고')}: {config.stock_count}{t('개')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="cart-item-actions">
                        <div className="cart-item-quantity">
                          <div className="cart-quantity-control">
                            <button 
                              className="quantity-decrease" 
                              onClick={() => handleDecreaseQuantity(item)}
                              disabled={count <= 1}
                            >-</button>
                            <span className="quantity-display">{localQuantities[item.id] || count}</span>
                            <button 
                              className="quantity-increase" 
                              onClick={() => handleIncreaseQuantity(item)}
                            >+</button>
                          </div>
                        </div>
                        <div className="cart-item-total">
                          {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                          <PriceDisplay price={price * count} />
                        </div>
                        <div className="cart-item-remove">
                          <button 
                            onClick={() => handleRemoveFromCart(item.id)}
                            disabled={loading}
                            aria-label={t('상품 삭제')}
                          >
                            <i className="fa fa-times" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* 장바구니 요약 및 결제 정보 */}
          <div className="col-lg-4 col-md-12">
            <div className="cart-summary">
              <div className="cart-summary-inner">
                <div className="summary-title">
                  <h3>{t('장바구니 금액')}</h3>
                </div>
                <div className="summary-item">
                  <div className="item-label">{t('상품 금액')}</div>
                  <div className="item-value">
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                    <PriceDisplay price={calculations.subtotal} />
                  </div>
                </div>
                <div className="summary-item">
                  <div className="item-label">{t('배송비')}</div>
                  <div className="item-value">
                    {/* 무료배송 여부에 따라 배송비 표시 */}
                    {calculations.subtotal >= freeShippingLimit ? (
                      <span className="free-shipping">{t('무료배송')}</span>
                    ) : (
                      /* @ts-ignore - PriceDisplay는 price prop을 받는 컴포넌트입니다 */
                      <PriceDisplay price={shippingFee} />
                    )}
                  </div>
                </div>
                <div className="summary-total">
                  <div className="total-label">{t('총 결제금액')}</div>
                  <div className="total-value">
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 오류 무시 */}
                    <PriceDisplay price={calculations.total} />
                  </div>
                </div>
                <div className="summary-action">
                  <button 
                    className="checkout-button"
                    onClick={handleCheckout}
                  >
                    {t('결제하기')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicCartSkin;